import React, { useState, useEffect } from "react";
import { trackEvent } from "@/lib/analytics";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Sparkles, ArrowLeft, Link2, Layers, ImageIcon, Languages , ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { TrustBadge } from "@/components/TrustBadge";
import { ArticleCard } from "@/components/ArticleCard";
import { useI18n, formatRelative, formatExact, itemDate } from "@/i18n";
import { ReportIssue } from "@/components/ReportIssue";
import { CategoryIcon } from "@/components/CategoryTag";
import { ShareButton } from "@/components/ShareCard";
import { ReadersCount } from "@/components/ReadersCount";

export default function ArticlePage() {
  const { id } = useParams();
  const { t, catName, lang, title } = useI18n();
  const [showAr, setShowAr] = useState(lang === "ar");
  const [trLoading, setTrLoading] = useState(false);
  const [arData, setArData] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["article", id],
    queryFn: async () => (await api.get(`/articles/${id}`)).data,
  });

  const needsAr = lang === "ar" && data?.article && ((data.article.title && !data.article.title_ar) || (data.article.summary && !data.article.summary_ar)) && !arData;
  useEffect(() => {
    setShowAr(lang === "ar");
    if (!needsAr) return;
    setTrLoading(true);
    api.post(`/articles/${id}/translate?lang=ar`)
      .then(({ data: tr }) => setArData(tr))
      .catch(() => {})
      .finally(() => setTrLoading(false));
  }, [lang, id, needsAr]);

  if (isLoading) return <div className="pt-32 text-center text-muted-foreground">{t("common.loading")}</div>;
  if (isError || !data) return <div className="pt-32 text-center text-muted-foreground">{t("common.notFound")}</div>;

  const a = data.article;
  const cluster = data.cluster_sources || [];
  const related = data.related || [];

  const arSummary = arData?.summary_ar || a.summary_ar;
  const arKeyPoints = arData?.key_points_ar || a.key_points_ar;
  const usingAr = showAr && arSummary;
  const displaySummary = usingAr ? arSummary : a.summary;
  const displayKeyPoints = usingAr ? arKeyPoints : a.key_points;

  const toggleTranslate = async () => {
    if (showAr) { setShowAr(false); return; }
    if (arSummary) { setShowAr(true); return; }
    setTrLoading(true);
    try {
      const { data: tr } = await api.post(`/articles/${id}/translate?lang=ar`);
      setArData(tr);
      setShowAr(true);
    } catch { /* keep original */ }
    finally { setTrLoading(false); }
  };

  return (
    <div className="pt-16">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-4">
        <Link to="/latest" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-violet-700 mb-6">
          <ArrowLeft size={15} className="rtl:rotate-180" /> {t("common.backToFeed")}
        </Link>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {a.categories?.map((c) => (
            <Link key={c} to={`/category/${c}`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-violet-700 bg-violet-50 px-2 py-0.5 rounded hover:bg-violet-100">
              <CategoryIcon slug={c} size={12} /> {catName(c)}
            </Link>
          ))}
          <TrustBadge status={a.status} mode={a.display_mode} />
        </div>

        <h1 data-testid="article-headline" className="font-display font-extrabold text-3xl sm:text-4xl leading-tight tracking-tight">{(showAr && (arData?.title_ar || a.title_ar)) || a.title || `${t("common.storyFrom")} ${a.source_name}`}</h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
          <span className="font-semibold text-slate-800">{a.source_name}</span>
          {a.author && <span>{t("common.by")} {a.author}</span>}
          <span title={formatExact(itemDate(a), lang)}>{!a.published_at && `${t("common.discovered")} · `}{formatRelative(itemDate(a), lang)}</span>
        </div>

        {a.image_url && (
          <figure className="mt-6">
            <div className="rounded-xl overflow-hidden bg-slate-100 aspect-[16/9] relative">
              <img src={a.image_url} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.parentElement.style.display = "none")} />
              <span className="absolute bottom-2 start-2 inline-flex items-center gap-1 bg-black/55 text-white text-[10px] font-mono px-2 py-0.5 rounded">
                <ImageIcon size={10} /> {t("image.licensed")}
              </span>
            </div>
            <figcaption className="mt-1.5 text-[11px] text-muted-foreground">
              {t("image.illustration")} · {a.image_attribution || "Unsplash"} · {a.image_license || "Unsplash License"}
            </figcaption>
          </figure>
        )}


        {a.summary && <div className="mt-8 bg-violet-50/60 border border-violet-100 rounded-xl p-5 sm:p-6">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-violet-700">
              <Sparkles size={12} /> {t("common.aiSummary")}
            </span>
            <button data-testid="translate-summary-btn" onClick={toggleTranslate} disabled={trLoading} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-violet-700 hover:text-violet-900 disabled:opacity-60">
              <Languages size={13} /> {trLoading ? t("article.translating") : (showAr && arSummary ? t("article.showEnglish") : t("article.translate"))}
            </button>
          </div>
          <p className="text-[15px] text-slate-800 leading-relaxed" dir={usingAr ? "rtl" : "ltr"}>{displaySummary}</p>
          {!usingAr && lang === "ar" && <p className="mt-2 text-[11px] text-amber-700">{t("article.englishNote")}</p>}
          <p className="mt-3 text-[11px] text-violet-700/70">{t("article.aiDisclaimer")}</p>
        </div>}

        {displayKeyPoints?.length > 0 && (
          <div className="mt-6">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3">{t("common.keyPoints")}</h2>
            <ul className="space-y-2">
              {displayKeyPoints.map((k, i) => (
                <li key={i} className="flex gap-2.5 text-[15px] text-slate-700 leading-relaxed" dir={usingAr ? "rtl" : "ltr"}>
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0" />{k}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Citation box */}
        <div className="mt-8 border border-slate-200 rounded-xl p-5 bg-white">
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">
            <Link2 size={12} /> {t("common.sourceCitation")}
          </span>
          <dl className="text-sm space-y-1.5">
            <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">{t("common.source")}</dt><dd className="font-medium text-slate-800">{a.source_name}</dd></div>
            <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">{t("common.published")}</dt><dd data-testid="citation-published" className="font-mono text-[13px] text-slate-700">{a.published_at ? formatExact(a.published_at, lang) : <span className="font-sans text-xs text-muted-foreground">{t("common.publishedPending")}</span>}</dd></div>
            {a.last_updated && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">{t("common.updated")}</dt><dd className="font-mono text-[13px] text-slate-700">{formatExact(a.last_updated, lang)}</dd></div>}
            {a.retrieved_at && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">{t("common.discovered")}</dt><dd className="font-mono text-[13px] text-slate-700">{formatExact(a.retrieved_at, lang)}</dd></div>}
            <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">{t("common.originalUrl")}</dt><dd className="truncate"><a href={a.original_url} target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:underline break-all">{a.original_url}</a></dd></div>
          </dl>
          <a href={a.original_url} target="_blank" rel="noopener noreferrer" data-testid="btn-read-original" onClick={() => trackEvent("read_original", { source: a.source_name, article_id: a.id })} className="mt-4 inline-flex items-center gap-2 bg-violet-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-violet-700 transition-colors">
            {t("common.readOriginalArticle")}
          </a>
          <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">{t("common.notAuthored")}</p>
        </div>

        <p data-testid="content-review-note" className="mt-6 flex items-start gap-2 text-[13px] text-slate-600 border-s-2 border-violet-500 ps-3 leading-relaxed">
          <ShieldCheck size={16} className="shrink-0 mt-0.5 text-violet-600" /> {t("common.limitedDisplay")}
        </p>

        <ReadersCount articleId={a.id} />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <ShareButton article={a} variant="button" />
          <ReportIssue article={a} />
        </div>

        {cluster.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3 inline-flex items-center gap-2"><Layers size={17} className="text-violet-600" /> {t("common.alsoReportedBy")}</h2>
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl bg-white overflow-hidden">
              {cluster.map((c) => (
                <a key={c.id} href={c.original_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 p-4 hover:bg-violet-50/40 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 line-clamp-1">{title(c)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.source_name} · {formatRelative(itemDate(c), lang)}</div>
                  </div>
                  <ExternalLink size={14} className="text-violet-600 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 mt-6 border-t border-slate-200">
          <h2 className="font-display font-bold text-xl tracking-tight mb-5">{t("common.relatedStories")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((r, i) => <ArticleCard key={r.id} article={r} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
